import { faBroom } from '@fortawesome/pro-solid-svg-icons/faBroom';
import { GetItemCounts, useInfiniteMailBox } from '@ukpostbox/client-data';
import { BatchMailItemType } from '@ukpostbox/client-data/lib/Types/ForwardingBatchTypes';
import {
	Button,
	ButtonSize,
	ColourStateSolid,
	ContentBlock,
	ContentLoader,
	InfiniteLoadTrigger,
	ItemRowCheckbox,
	ItemRowHeader,
	Modal,
	ModalSize,
	ModalType,
	ToggleSwitch,
	ToggleType,
	useComboBoxSimple,
	useLayout,
} from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, { useEffect, useState } from 'react';
import { ItemRowHeaderCell } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/Cells/ItemRowHeaderCell';
import SelectEverythingBanner from '../../../_Global/Components/SelectEverythingBanner';
import ListViewMailItemMinimal from '../../../Mailboxes/Components/Mailbox/Entries/ListViewMailItemMinimal';

interface SearchMailItemsForBatchModalType extends ModalType {
	batchMailUuids: Array<string>;
}

function SearchMailItemsForBatchModal(props: SearchMailItemsForBatchModalType) {
	const { isTiny, isSmall, isMedium, isLarge } = useLayout();

	const {
		allSelected,
		selectedItems,
		visibleIndeterminate,
		visibleSelected,
		ToggleAll,
		ToggleVisible,
		ToggleSelected,
		ResetSelection,
	} = useComboBoxSimple();

	const {
		status,
		data,
		error,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		dataUpdatedAt,
		isStale,
		isRefetching,
	} = useInfiniteMailBox({
		folderId: 1,
		includeFolderAssigned: { value: true },
	});

	const [excludedItems, setExcludedItems] = useState<Array<string>>(props.batchMailUuids);
	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const AddSelectedToBatch = () => {
		const matchingItems: Array<BatchMailItemType> = [];

		selectedItems.forEach((itemId) => {
			const mailData: Array<any> = data?.pages?.map((page) => page.data?.map((mailItem) => mailItem))[0] || [];

			const mailIndex = GetArrayIndexByKeyValue(mailData, 'mailUuid', itemId);

			const mailItem: BatchMailItemType = {
				mailUuid: mailData[mailIndex].mailUuid,
				files: mailData[mailIndex].files,
				trackingId: mailData[mailIndex].trackingId,
				mailType: mailData[mailIndex].mailType,
				descriptions: [
					{
						id: 0,
						descriptionType: null,
						description: '',
						quantity: 0,
						value: 0,
						commodityCode: '',
						originCountryCode: '',
					},
				],
			};

			matchingItems.push(mailItem);
		});

		props.actionedCallback(matchingItems);
		ResetSelection();
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [dataUpdatedAt, data]);

	useEffect(() => {
		setExcludedItems(props.batchMailUuids);
	}, [props]);

	return (
		<Modal
			title='Add Mailboxes Items'
			isOpen={props.isOpen}
			closeCallback={props.closeCallback}
			modalSize={ModalSize.Large}
			footerContent={
				<Button
					disabled={selectedItems.length === 0}
					title='Add to Batch'
					fullWidth
					colour={ColourStateSolid.Success}
					onClick={() => AddSelectedToBatch()}
				/>
			}
		>
			<div className='mail-item-batch-filter-container'>
				<ItemRowHeader
					title='Search Mailboxes Items'
					extraClass={`mail-item-forward-preview-header ${
						selectedItems.length > 0 ? 'item-row-actions-active' : ''
					}`}
					sticky
				>
					<ItemRowCheckbox>
						<ToggleSwitch
							name='mailItem-all'
							label='mailItem-all'
							invalid={false}
							type={ToggleType.Checkbox}
							defaultChecked={allSelected || (!visibleIndeterminate && visibleSelected)}
							isIndeterminate={!allSelected && visibleIndeterminate}
							onChange={(e) => ToggleVisible(data, 'mailUuid', !visibleSelected)}
							hideLabel
						/>
					</ItemRowCheckbox>

					{selectedItems.length === 0 && (
						<>
							<ItemRowHeaderCell>Envelope</ItemRowHeaderCell>
							<ItemRowHeaderCell>Content</ItemRowHeaderCell>
							<ItemRowHeaderCell>Reference</ItemRowHeaderCell>
							{(!isTiny || !isSmall || !isMedium) && <ItemRowHeaderCell>Tags</ItemRowHeaderCell>}
							{(!isTiny || !isSmall || !isMedium || !isLarge) && (
								<ItemRowHeaderCell>Notes</ItemRowHeaderCell>
							)}
						</>
					)}

					{selectedItems.length > 0 && (
						<div>
							<Button
								title={`Clear Selection (${selectedItems.length})`}
								icon={faBroom}
								size={ButtonSize.Small}
								onClick={() => ResetSelection()}
							/>
						</div>
					)}

					{itemCounts.loaded !== itemCounts.total &&
						selectedItems.length !== itemCounts.total &&
						visibleSelected && (
							<SelectEverythingBanner
								selectedCount={allSelected ? itemCounts.total : selectedItems.length}
								selectedLabel={
									allSelected ? 'Select loaded only' : `Select ${itemCounts.total} available items`
								}
								callback={() => ToggleAll(!allSelected)}
							/>
						)}
				</ItemRowHeader>

				{status === 'pending' ? (
					<ContentLoader childHeight='7em' />
				) : status === 'error' ? (
					<ContentBlock error title={error.name} content={error.message} />
				) : (
					<>
						{data.pages.map((group, groupIndex) =>
							group.data.map((mailItem, index) => (
								<ListViewMailItemMinimal
									key={`mailItemMinimal${mailItem.mailUuid}`}
									index={`${groupIndex}_${index}`}
									selected={allSelected || selectedItems.includes(mailItem.mailUuid)}
									selectable
									callBack={(mailUuid: any) => ToggleSelected(mailUuid, itemCounts.total)}
									disabled={excludedItems.includes(mailItem.mailUuid)}
									forwardingPreview
									{...mailItem}
								/>
							)),
						)}

						{itemCounts.total > 0 && (
							<InfiniteLoadTrigger
								loaded={itemCounts.loaded}
								totalCount={itemCounts.total}
								hasNextPage={hasNextPage}
								fetchNextPage={fetchNextPage}
								isStale={isStale}
								isRefetching={isRefetching}
								isFetchingNextPage={isFetchingNextPage}
							/>
						)}
					</>
				)}
			</div>
		</Modal>
	);
}

export { SearchMailItemsForBatchModal };
